import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { X, Upload, Image as ImageIcon, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/services/axios";
import { getApiBaseUrl } from '@/config/production';
import { SessionManager } from '@/utils/sessionManager';
import { useUser } from '@/contexts/UserContext';

const API_BASE = getApiBaseUrl();


interface UploadFile {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  id: string;
}

// Interface específica para upload de fotos
interface EventForUpload {
  id: string;
  title: string;
  name?: string;
  date: string;
  location?: string;
}

export default function PhotoUpload() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();
  const { user, isLoggedIn } = useUser();

  const handleUpload = async () => {
    if (!selectedEventId || files.length === 0) {
      toast({
        title: "Dados incompletos",
        description: "Selecione um evento e adicione pelo menos uma foto.",
        variant: "destructive",
      });
      return;
    }

    if (!isLoggedIn || !user) {
      toast({
        title: "Autenticação necessária",
        description: "Você precisa estar logado para fazer upload de fotos.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();

      // Adicionar cada arquivo ao FormData
      files.forEach((file) => {
        formData.append('photos', file.file);
      });

      // Adicionar dados adicionais
      formData.append('eventId', selectedEventId);
      formData.append('photographerId', user.id); // Usar o ID do usuário logado

      // Simular progresso durante o upload
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      // Obter token do localStorage
      const token = SessionManager.getToken();

      const response = await fetch(`${API_BASE}/api/photos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      // Limpar arquivos após upload bem-sucedido
      setFiles([]);
      setSelectedEventId('');
      setDescription('');

      toast({
        title: "Upload concluído!",
        description: `${result.data?.length || 0} fotos enviadas com sucesso!`,
      });

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast({
        title: "Erro no upload",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Buscar eventos disponíveis
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/events');
        console.log('Resposta da API:', response.data);
        return response.data;
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
        return { events: [] };
      }
    }
  });

  // Garantir que events seja sempre um array
  const events = Array.isArray(eventsData?.events) ? eventsData.events : [];

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending' as const,
      progress: 0,
      id: Math.random().toString(36).substr(2, 9)
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const removeFile = (id: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Upload de Fotos</h1>
        <p className="text-muted-foreground">
          Faça o upload das suas fotos do evento para que os atletas possam visualizar e adquirir.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Seleção de Evento */}
        <Card>
          <CardHeader>
            <CardTitle>Informações do Upload</CardTitle>
            <CardDescription>
              Selecione o evento e adicione uma descrição opcional.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="event">Evento *</Label>
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um evento" />
                </SelectTrigger>
                <SelectContent>
                  {eventsLoading ? (
                    <SelectItem value="loading" disabled>
                      Carregando eventos...
                    </SelectItem>
                  ) : events.length > 0 ? (
                    events.map((event: EventForUpload) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name || event.title} - {new Date(event.date).toLocaleDateString('pt-BR')}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-events" disabled>
                      Nenhum evento encontrado
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                id="description"
                placeholder="Adicione uma descrição para as fotos..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Área de Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Selecionar Fotos</CardTitle>
            <CardDescription>
              Arraste e solte as fotos aqui ou clique para selecionar. Máximo 10MB por foto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              {isDragActive ? (
                <p className="text-lg font-medium">Solte as fotos aqui...</p>
              ) : (
                <div>
                  <p className="text-lg font-medium mb-2">Clique ou arraste fotos para cá</p>
                  <p className="text-sm text-muted-foreground">
                    Suporta: JPEG, PNG, WebP (máx. 10MB cada)
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview das Fotos */}
        {files.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Fotos Selecionadas ({files.length})</CardTitle>
              <CardDescription>
                Visualize as fotos antes de fazer o upload.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {files.map((uploadFile) => (
                  <div key={uploadFile.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                      <img
                        src={uploadFile.preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeFile(uploadFile.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>

                    {/* File Info */}
                    <div className="mt-2 text-xs text-muted-foreground truncate">
                      {uploadFile.file.name}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Botão de Enviar */}
        {files.length > 0 && selectedEventId && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Pronto para enviar</h3>
                    <p className="text-sm text-muted-foreground">
                      {files.length} foto{files.length > 1 ? 's' : ''} para o evento selecionado
                    </p>
                  </div>
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="min-w-[120px]"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Enviar Fotos
                      </>
                    )}
                  </Button>
                </div>

                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Enviando fotos...</span>
                      <span className="text-sm text-muted-foreground">{Math.round(uploadProgress)}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                {description && (
                  <div className="text-sm text-muted-foreground">
                    <strong>Descrição:</strong> {description}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}